import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { producerMe } from '../../lib/producer';
import { operatorApi } from '../../lib/api';
import { Turnstile } from '../../components/Turnstile';
import { Pegasus } from '../../components/DreamlinkMark';

const MAROON = '#590b11';
const CREAM = '#F5F2EE';
const INK = '#262626';
const BROWN = '#6F5C53';
const DISPLAY = "'Archivo', 'DM Sans', sans-serif";

type State = 'idle' | 'sending' | 'sent' | 'error' | 'captcha';

export function OperatorLogin() {
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [token, setToken] = useState<string | null>(null);
  const [state, setState] = useState<State>('idle');
  const urlError = params.get('error');
  const navigate = useNavigate();

  // A producer-only teammate who is already signed in (e.g. followed an old
  // /operator link that 401'd) belongs on their Producer tab, not this form.
  useEffect(() => {
    producerMe().then(me => { if (me && !me.operator) navigate('/operator/producer', { replace: true }); });
  }, [navigate]);

  const onVerify = useCallback((t: string) => setToken(t), []);
  const onExpire = useCallback(() => setToken(null), []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!token) return;
    setState('sending');
    const r = await operatorApi.requestMagicLink(email.trim().toLowerCase(), token);
    if (r.status === 403) return setState('captcha');
    if (r.status >= 400 && r.status !== 429) return setState('error');
    // Always-ok (no enumeration): show the same confirmation regardless.
    setState('sent');
  }

  return (
    <div style={{ minHeight: '100vh', background: CREAM, color: INK, fontFamily: "'DM Sans', sans-serif", fontWeight: 400, display: 'grid', placeItems: 'center', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: MAROON, justifyContent: 'center', marginBottom: 14 }}>
          <Pegasus size={24} color={MAROON} />
          <span style={{ fontFamily: DISPLAY, fontWeight: 700, letterSpacing: '0.02em', fontSize: 17 }}>
            Dreamlink <span style={{ fontWeight: 400, opacity: 0.7 }}>Studio</span>
          </span>
        </div>
        <h1 style={{ fontFamily: DISPLAY, fontWeight: 600, fontSize: 30, letterSpacing: '-0.02em', textAlign: 'center', margin: '0 0 6px' }}>Studio staff sign in</h1>
        <p style={{ textAlign: 'center', color: BROWN, fontSize: 14, margin: '0 0 28px' }}>
          We email a one-time sign-in link to Dreamlink staff.
        </p>

        {state === 'sent' ? (
          <div style={{ background: '#fff', border: `0.5px solid rgba(38,38,38,0.14)`, borderRadius: 12, padding: 28, textAlign: 'center' }}>
            <div style={{ fontFamily: DISPLAY, fontWeight: 600, fontSize: 22, letterSpacing: '-0.01em', color: MAROON, marginBottom: 8 }}>Check your email.</div>
            <p style={{ fontSize: 14, color: BROWN, margin: 0 }}>
              If {email} is an approved operator, a sign-in link is on its way. It expires in 15 minutes.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} style={{ background: '#fff', border: `0.5px solid rgba(38,38,38,0.14)`, borderRadius: 12, padding: 28, display: 'flex', flexDirection: 'column', gap: 16 }}>
            <label style={{ fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', color: BROWN }}>
              Work email
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@dreamlink.global"
                autoComplete="email"
                style={{ marginTop: 6, width: '100%', padding: '11px 12px', border: '1px solid rgba(38,38,38,0.18)', borderRadius: 6, fontSize: 15, fontFamily: 'inherit', background: CREAM, color: INK }}
              />
            </label>
            <Turnstile onVerify={onVerify} onExpire={onExpire} theme="light" />
            {(state === 'error' || urlError === 'invalid_or_expired') && (
              <div style={{ color: '#B3261E', fontSize: 13 }}>That link was invalid or expired. Request a new one.</div>
            )}
            {urlError === 'not_authorized' && (
              <div style={{ color: '#B3261E', fontSize: 13 }}>That email is not an approved operator.</div>
            )}
            {state === 'captcha' && <div style={{ color: '#B3261E', fontSize: 13 }}>Captcha failed, try again.</div>}
            <button
              type="submit"
              disabled={!token || state === 'sending'}
              style={{ padding: '13px 20px', background: MAROON, color: CREAM, border: 'none', borderRadius: 4, fontSize: 15, fontWeight: 500, letterSpacing: '0.04em', cursor: !token || state === 'sending' ? 'not-allowed' : 'pointer', opacity: !token || state === 'sending' ? 0.5 : 1 }}
            >
              {state === 'sending' ? 'Sending…' : 'Email me a sign-in link'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
